import { API } from '~apis/axios/instance';
import { PlanInfoItem } from '~apis/billing';
import { ResponseBaseWithData } from '~types/response';

export enum OnboardingStep {
  INIT,
  // @deprecated
  CERTIFICATE_REGISTRATION,
  // @deprecated
  CORP_CARD_REGISTRATION,
  // @deprecated
  START_ANALYSIS,
  // @deprecated
  ANALYSIS_COMPLETE,
  // @deprecated
  ANALYSIS_FAILED,
  BANK_CERTIFICATE_REGISTRATION,
  NTS_CERTIFICATE_REGISTRATION,
  ONBOARDED,
}

export type Trial = {
  startDate: string;
  endDate: string;
} | null;

interface SettingResponse {
  onboardingStep: keyof typeof OnboardingStep;
  trial: Trial;
  activePlan: PlanInfoItem | null;
  autoScraping: {
    enabled: boolean;
    until?: string;
  };
  features: {
    customerPlan: boolean;
    wiring: boolean;
  };
  pendingMandateRequest: {
    taxClientId: number;
    taxAgentId: string;
    taxAgentName: string;
  } | null;
}

export function getSettings(companyId: string) {
  return API.get<ResponseBaseWithData<SettingResponse>>(`/api/v3/companies/${companyId}/settings`);
}

export function setOnboardingSetting({
  companyId,
  step,
}: {
  companyId: string;
  step: keyof typeof OnboardingStep;
}) {
  return API.patch<ResponseBaseWithData<SettingResponse>>(
    `/api/v3/companies/${companyId}/settings/onboarding-step`,
    {
      data: {
        step,
      },
    },
  );
}

export const autoScrapeOn = ({ companyId }: { companyId: string }) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/settings/auto-scraping/on`,
  );
};

export const autoScrapeOff = ({ companyId }: { companyId: string }) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/settings/auto-scraping/off`,
  );
};

export enum OnboardingStepStatus {
  SUCCESS = 'SUCCESS',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  UNSUPPORTED_FEATURE = 'UNSUPPORTED_FEATURE',
}

export interface OnboardingStatusResponse {
  step: OnboardingStepStatus;
  bankAccountRegistered: boolean;
  ntsCertificateRegistered: boolean;
  hasInvitation: boolean;
  labelStats: ILabelStats;
}
interface ILabelStats {
  totalCount: number;
  labeledCount: number;
  labeledRatio: number;
}

export const getOnboardingStatus = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<OnboardingStatusResponse>>(
    `/api/v3/companies/${companyId}/settings/onboarding-status`,
  );
};

export const acceptMandateRequest = ({
  companyId,
  taxClientId,
}: {
  companyId: string;
  taxClientId: string;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/mandate/tax-clients/${taxClientId}/accept`,
  );
};

export const rejectMandateRequest = ({
  companyId,
  taxClientId,
}: {
  companyId: string;
  taxClientId: string;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/mandate/tax-clients/${taxClientId}/reject`,
  );
};

export interface TaxAgentResponse {
  taxClientId: number;
  taxAgentId: string;
  taxAgentName: string;
  businessRegNo: string;
  assignees: [
    {
      userId: string;
      name: string;
      email: string;
      phone: string;
      isPrimary: boolean;
    },
  ];
}

export const getTaxAgent = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<TaxAgentResponse>>(
    `/api/v3/companies/${companyId}/mandate/tax-agent`,
  );
};

export const deleteTaxAgent = ({
  companyId,
  taxClientId,
}: {
  companyId: string;
  taxClientId: string;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/mandate/tax-clients/${taxClientId}/terminate`,
  );
};
